import PLACES from './places.json';
import { slugify } from '../../shared/slug.mjs';


/**
 * Which city and venue page an event belongs to. Manchester, NH is not Manchester, England and
 * the House of Blues in Chicago is not the one in Houston: a name several places share has a
 * page per place, and the build decides which (scripts/places.mjs, which writes places.json
 * every build). This is the build's rule, step for step — keep the two in step.
 */
type CityRow = [slug: string, cc: string, states: string[], lat: number | null, lng: number | null];
type VenueRow = [slug: string, cities: string[]];
// packed by the build: a slug as its suffix ("" = the plain URL), states as one string
const P = PLACES as unknown as {
  a: Record<string, string>;
  c: Record<string, [string, string, string, number | null, number | null][]>;
  w: Record<string, string>;
  v: Record<string, [string, string[]][]>;
};
const full = (base: string, suffix: string) => (suffix ? `${base}-${suffix}` : base);
const T = {
  a: P.a,
  c: Object.fromEntries(Object.entries(P.c).map(([b, rows]) => [b, rows.map(([s, cc, st, lat, lng]): CityRow => [full(b, s), cc, st ? st.split(' ') : [], lat, lng])])),
  w: P.w,
  v: Object.fromEntries(Object.entries(P.v).map(([b, rows]) => [b, rows.map(([s, cities]): VenueRow => [full(b, s), cities])])),
};

const BY_STATE = new Set(['US', 'CA', 'AU']);
const stateOf = (cc: string, region?: string) => {
  const r = String(region ?? '').trim().toUpperCase();
  return BY_STATE.has(cc) && /^[A-Z]{2,3}$/.test(r) ? r : null;
};
const km = (aLat: number, aLng: number, bLat: number, bLng: number) => {
  const t = Math.PI / 180;
  const x = Math.sin(((bLat - aLat) * t) / 2) ** 2 + Math.cos(aLat * t) * Math.cos(bLat * t) * Math.sin(((bLng - aLng) * t) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(Math.min(1, x)));
};

const pickCity = (places: CityRow[], cc: string, region: string | undefined, lat: number, lng: number): CityRow => {
  let c = places.filter((p) => p[1] === cc);
  if (!c.length) return places[0];
  if (c.length === 1) return c[0];
  const st = stateOf(cc, region);
  if (st) {
    const same = c.filter((p) => p[2].includes(st));
    if (same.length === 1) return same[0];
    if (same.length) c = same;
  }
  if (lat || lng) {
    let best: [CityRow, number] | null = null;
    for (const p of c) {
      if (p[3] === null || p[4] === null) continue;
      const d = km(lat, lng, p[3], p[4]);
      if (!best || d < best[1]) best = [p, d];
    }
    if (best) return best[0];
  }
  return c[0];
};

type Placed = { city: string; venue?: string; country?: string; lat?: number; lng?: number; place?: { region?: string } };

/** The slug of an event's city page: /city/<slug>. */
export const citySlugOf = (e: Placed): string => {
  const raw = slugify(e.city ?? '');
  const base = T.a[raw] ?? raw;
  const places = T.c[base];
  if (!places) return base;
  const lat = Number(e.lat) || 0;
  const lng = Number(e.lng) || 0;
  return pickCity(places, e.country || 'US', e.place?.region, lat, lng)[0];
};

/** The slug of an event's venue page: /venue/<slug>. */
export const venueSlugOf = (e: Placed): string => {
  const raw = slugify(e.venue ?? '');
  const base = T.w[raw] ?? raw;
  const places = T.v[base];
  if (!places) return base;
  const city = citySlugOf(e);
  return (places.find((p) => p[1].includes(city)) ?? places[0])[0];
};
